<template>
  <div
    v-if="modelValue"
    class="ijd-overlay"
    data-testid="import-job-description-overlay"
  >
    <div class="ijd-overlay__shell">
      <IQControlledOverlayHeader :title="headerTitle" @close="handleClose" />

      <div class="ijd-overlay__body" :class="{ 'ijd-overlay__body--split': previewOpen }">
        <section class="ijd-chat" :class="{ 'ijd-chat--split': previewOpen }">
          <div class="ijd-chat__pinned">
            <UploadedFileCardWithViewer
              v-if="uploadedFile?.name"
              :file-name="uploadedFile.name"
              :file-type="uploadedFile.type"
              :is-preview-open="previewOpen"
              @open-viewer="previewOpen = true"
            />
          </div>

          <div ref="threadRef" class="ijd-chat__thread" data-testid="ijd-chat-thread">
            <IQThinkingBanner
              v-if="phase === IMPORT_JD_PHASE.ANALYZING"
              data-testid="ijd-thinking"
              state="thinking"
              title="IQ is looking for Job Description Information and is validating against context ..."
              caption="Analyzing your file ..."
              :reasoning="liveReasoning"
            />

            <template v-else>
              <div
                v-for="message in messages"
                :key="message.id"
                class="ijd-turn"
                :class="`ijd-turn--${message.kind}`"
              >
                <div v-if="message.kind === 'ai'" class="ijd-ai">
                  <span class="ijd-ai__icon" aria-hidden="true">
                    <Icon name="iq_small_icon" />
                  </span>
                  <div class="ijd-ai__body">
                    <p class="ijd-ai__text">{{ message.text }}</p>
                    <div v-if="message.actions?.length" class="ijd-ai__actions">
                      <span
                        v-for="action in message.actions"
                        :key="action.type"
                        :data-testid="`ijd-action-${action.type}`"
                      >
                        <StandardButton
                          type="magenta"
                          :variant="action.variant"
                          size="small"
                          :label="action.label"
                          @click="handleAction(action.type)"
                        />
                      </span>
                    </div>
                    <div
                      v-if="message.showDropdown"
                      class="ijd-ai__dropdown"
                      data-testid="ijd-dropdown"
                    >
                      <JobDescriptionImportActionsDropDown
                        :bullet-text="activeBullet.extracted"
                        :matched-responsibility-id="activeMatchedId"
                        :covering-roles="activeCoveringRoles"
                        @attach-role="handleDropdownAction(IMPORT_JD_ACTION.ATTACH_ROLE, $event)"
                        @attach-direct="handleDropdownAction(IMPORT_JD_ACTION.ATTACH_DIRECT, $event)"
                        @create-new="handleDropdownAction(IMPORT_JD_ACTION.CREATE_NEW, $event)"
                        @discard="handleDropdownAction(IMPORT_JD_ACTION.DISCARD)"
                      />
                    </div>
                  </div>
                </div>

                <div v-else class="ijd-user">
                  <span class="ijd-user__bubble">{{ message.text }}</span>
                </div>
              </div>

              <div
                v-if="actionPending"
                class="ijd-turn ijd-turn--ai"
                data-testid="ijd-action-pending"
              >
                <div class="ijd-ai ijd-ai--pending">
                  <IQThinkingPulsatingIcon state="thinking" :size="24" />
                </div>
              </div>
            </template>
          </div>
        </section>

        <aside
          v-if="previewOpen"
          class="ijd-preview"
          data-testid="ijd-document-preview"
        >
          <button
            type="button"
            class="ijd-preview__close"
            aria-label="Close document preview"
            data-testid="ijd-preview-close"
            @click="previewOpen = false"
          >
            <img :src="closeCircleSrc" alt="" width="18" height="18" />
          </button>
          <div class="ijd-preview__body">
            <FilePreview :file="uploadedFile" />
          </div>
        </aside>
      </div>
    </div>

    <AddResponsibility
      :value="showCreateModal"
      :initialValue="createInitialName"
      :initialContext="createInitialContext"
      :isShowCharCounter="true"
      :max_char="120"
      :showContext="true"
      :loading="createLoading || createSaving"
      :suggestedByIQ="createSuggestedByIQ"
      :errorMessage="createErrorMessage"
      :zIndex="11000"
      @input="handleCreateModalInput"
      @onSave="handleCreateModalSave"
      @clearError="createErrorMessage = ''"
    />
    <DiscardProposedChangesDialog
      :value="showDiscardModal"
      :z-index="11000"
      @input="handleDiscardModalInput"
      @onAction="handleDiscardModalConfirm"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onUnmounted, ref, watch } from "vue";
import IQControlledOverlayHeader from "@components/IQControlledOverlayHeader/IQControlledOverlayHeader.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import IQThinkingPulsatingIcon from "@components/IQThinkingPulsatingIcon/IQThinkingPulsatingIcon.vue";
import FilePreview from "@components/FilePreview/FilePreview.vue";
import UploadedFileCardWithViewer from "@components/UploadedFileCardWithViewer/UploadedFileCardWithViewer.vue";
import JobDescriptionImportActionsDropDown from "@components/JobDescriptionImportActionsDropDown/JobDescriptionImportActionsDropDown.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import AddResponsibility from "@components/RolesCatalog/AddResponsibilityDialog/AddResponsibilityDialog.vue";
import DiscardProposedChangesDialog from "@components/DiscardProposedChangesDialog/DiscardProposedChangesDialog.vue";
import {
  DEMO_BULLETS,
  IMPORT_JD_ACTION,
  IMPORT_JD_ANALYZE_MS,
  IMPORT_JD_PHASE,
  IMPORT_JD_SUGGEST_TIMEOUT_MS,
  buildAttachDirectResult,
  buildAttachRoleResult,
  buildCreateNewResult,
  buildDiscardResult,
  buildDoneText,
  buildEmptyDocumentText,
  buildExtractionIntro,
  buildFallbackSuggestion,
  buildFirstLiveMatchingText,
  buildTitleMismatchText,
  resolveDemoOutcome,
  userActionLabel,
} from "./importJobDescriptionDemoFlow.js";
import {
  extractJobDescriptionStream,
  executeJobDescriptionSolution,
  suggestJobDescriptionResponsibility,
} from "@/api/positionAPI";
import { LD_FLAG_IDEA_MDP_585 } from "@components/constants/launchDarklyFlagKeys";
import { getResponsibilityCreateErrorMessage } from "@components/utils/responsibilityNameError";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  positionId: {
    type: [String, Number],
    default: null,
  },
  positionName: {
    type: String,
    default: "",
  },
  uploadedFile: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(["close", "request-new-file"]);

const proxy = getCurrentInstance()?.proxy;

const closeCircleSrc = require("@components/assets/x-circle.svg");

const previewOpen = ref(true);
const phase = ref(IMPORT_JD_PHASE.ANALYZING);
const messages = ref([]);
const bulletIndex = ref(0);
const liveMatches = ref([]);
const liveTotal = ref(0);
// ER-11669: the backend's own extraction-success copy (ER-11668's
// `message` field), rendered verbatim as the intro line of the first
// live-session AI turn instead of building it client-side.
const liveExtractMessage = ref("");
// ER-11678: accumulated `event: thought` reasoning from the SSE import stream,
// rendered verbatim by IQThinkingBanner's `reasoning` prop.
const liveReasoning = ref("");
const usingLiveApi = ref(false);
const threadRef = ref(null);
const showCreateModal = ref(false);
const showDiscardModal = ref(false);
const createInitialName = ref("");
const createInitialContext = ref("");
const createLoading = ref(false);
const createSuggestedByIQ = ref(false);
const createSaving = ref(false);
const createErrorMessage = ref("");
const actionPending = ref(false);
let createConfirmed = false;
let discardConfirmed = false;
let analyzeTimer = null;
let messageSeq = 0;
let extractGeneration = 0;
let extractAbortController = null;
let suggestGeneration = 0;
let suggestTimer = null;
let suggestAbortController = null;

const headerTitle = computed(() => {
  const id = props.positionId != null && props.positionId !== "" ? `#${props.positionId}` : "";
  const name = props.positionName || "";
  const target = [id, name].filter(Boolean).join(" - ");
  return target
    ? `AI assisted Job Description Import for position ${target}`
    : "AI assisted Job Description Import";
});

const activeBullet = computed(() => {
  if (usingLiveApi.value) {
    return liveMatches.value[bulletIndex.value] || liveMatches.value[0] || {};
  }
  return DEMO_BULLETS[bulletIndex.value] || DEMO_BULLETS[0];
});

const activeMatchedId = computed(
  () =>
    activeBullet.value?.matchedResponsibilityId ??
    activeBullet.value?.matched_responsibility_id ??
    null
);

const activeCoveringRoles = computed(
  () => activeBullet.value?.coveringRoles || activeBullet.value?.covering_roles || []
);

watch(
  () => {
    const blob = uploadedFileBlob();
    return [
      props.modelValue,
      props.uploadedFile?.name || "",
      blob?.size || 0,
      blob?.lastModified || 0,
    ];
  },
  ([open, name]) => {
    if (!open) {
      resetSession();
      return;
    }
    if (name) startAnalysis();
  },
  { immediate: true }
);

onUnmounted(() => {
  clearAnalyzeTimer();
  abortExtractStream();
  invalidateCreateSuggestion();
});

function nextId() {
  messageSeq += 1;
  return `ijd-${messageSeq}`;
}

function pushAi(payload) {
  messages.value.push({
    id: nextId(),
    kind: "ai",
    text: payload.text,
    actions: payload.actions || [],
    showDropdown: !!payload.showDropdown,
  });
  scrollThread();
}

function pushUser(text) {
  const id = nextId();
  messages.value.push({
    id,
    kind: "user",
    text,
  });
  scrollThread();
  return id;
}

function removeMessage(id) {
  messages.value = messages.value.filter((message) => message.id !== id);
}

async function scrollThread() {
  await nextTick();
  const el = threadRef.value;
  if (el) el.scrollTop = el.scrollHeight;
}

function clearAnalyzeTimer() {
  if (analyzeTimer) {
    clearTimeout(analyzeTimer);
    analyzeTimer = null;
  }
}

function abortExtractStream() {
  if (extractAbortController) {
    extractAbortController.abort();
    extractAbortController = null;
  }
}

function uploadedFileBlob() {
  const uploaded = props.uploadedFile;
  if (!uploaded) return null;
  if (typeof File !== "undefined" && uploaded instanceof File) return uploaded;
  return uploaded.file || null;
}

function resetSession() {
  extractGeneration += 1;
  clearAnalyzeTimer();
  abortExtractStream();
  invalidateCreateSuggestion();
  phase.value = IMPORT_JD_PHASE.ANALYZING;
  messages.value = [];
  bulletIndex.value = 0;
  liveMatches.value = [];
  liveTotal.value = 0;
  liveExtractMessage.value = "";
  liveReasoning.value = "";
  usingLiveApi.value = false;
  previewOpen.value = true;
  messageSeq = 0;
  showCreateModal.value = false;
  showDiscardModal.value = false;
  createInitialName.value = "";
  createInitialContext.value = "";
  createLoading.value = false;
  createSuggestedByIQ.value = false;
  createSaving.value = false;
  createErrorMessage.value = "";
  actionPending.value = false;
  createConfirmed = false;
  discardConfirmed = false;
}

function startAnalysis() {
  resetSession();
  phase.value = IMPORT_JD_PHASE.ANALYZING;
  if (uploadedFileBlob() && props.positionId != null) {
    extractLive();
    return;
  }
  analyzeTimer = setTimeout(() => {
    finishAnalysis();
  }, IMPORT_JD_ANALYZE_MS);
}

async function fetchExtractData(controller, generation) {
  const { data } = await extractJobDescriptionStream({
    positionId: props.positionId,
    file: uploadedFileBlob(),
    signal: controller.signal,
    onThinkingUpdate: (reasoning) => {
      if (generation === extractGeneration) {
        liveReasoning.value = reasoning;
      }
    },
  });
  return data;
}

function applyExtractResult(data) {
  liveMatches.value = Array.isArray(data?.matches) ? data.matches : [];
  liveTotal.value = data?.total ?? (liveMatches.value.length || (data?.bullets || []).length);
  liveExtractMessage.value = data?.message || "";
  if (data?.empty || liveMatches.value.length === 0) {
    phase.value = IMPORT_JD_PHASE.EMPTY;
    pushAi({
      text: data?.message || buildEmptyDocumentText(),
      actions: [
        { type: IMPORT_JD_ACTION.UPLOAD_NEW_FILE, label: "Upload a new file", variant: "primary" },
        { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
      ],
    });
    return;
  }
  if (data?.title_mismatch) {
    phase.value = IMPORT_JD_PHASE.TITLE_MISMATCH;
    pushAi({
      text: buildTitleMismatchText(
        props.positionName,
        data?.jd_title || data?.title || undefined
      ),
      actions: [
        { type: IMPORT_JD_ACTION.CONTINUE_AS_IS, label: "Continue As Is", variant: "primary" },
        { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
      ],
    });
    return;
  }
  startMatching();
}

const GENERIC_EXTRACT_ERROR = "Something went wrong while reading the file.";

async function extractLive() {
  const generation = extractGeneration;
  usingLiveApi.value = true;
  liveReasoning.value = "";
  abortExtractStream();
  extractAbortController = new AbortController();
  const controller = extractAbortController;
  let data;
  try {
    data = await fetchExtractData(controller, generation);
  } catch (error) {
    if (generation !== extractGeneration) return;
    if (controller.signal.aborted) return;
    console.error("Failed to extract job description:", error);
    phase.value = IMPORT_JD_PHASE.EMPTY;
    // An error frame means IQ rejected this document: retrying cannot help.
    // Any other failure (network, HTTP, truncated stream) can be retried by the user.
    if (error?.isErrorFrame) {
      pushAi({
        text: error.message || GENERIC_EXTRACT_ERROR,
        actions: [
          { type: IMPORT_JD_ACTION.UPLOAD_NEW_FILE, label: "Upload a new file", variant: "primary" },
          { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
        ],
      });
      return;
    }
    pushAi({
      text: GENERIC_EXTRACT_ERROR,
      actions: [
        { type: IMPORT_JD_ACTION.RETRY, label: "Retry", variant: "primary" },
        { type: IMPORT_JD_ACTION.UPLOAD_NEW_FILE, label: "Upload a new file", variant: "outline" },
        { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
      ],
    });
    return;
  } finally {
    if (extractAbortController === controller) {
      extractAbortController = null;
    }
  }
  if (generation !== extractGeneration) return;
  applyExtractResult(data);
}

function finishAnalysis() {
  const outcome = resolveDemoOutcome(props.uploadedFile?.name);
  if (outcome === "empty") {
    phase.value = IMPORT_JD_PHASE.EMPTY;
    pushAi({
      text: buildEmptyDocumentText(),
      actions: [
        { type: IMPORT_JD_ACTION.UPLOAD_NEW_FILE, label: "Upload a new file", variant: "primary" },
        { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
      ],
    });
    return;
  }
  if (outcome === "mismatch") {
    phase.value = IMPORT_JD_PHASE.TITLE_MISMATCH;
    pushAi({
      text: buildTitleMismatchText(props.positionName),
      actions: [
        { type: IMPORT_JD_ACTION.CONTINUE_AS_IS, label: "Continue As Is", variant: "primary" },
        { type: IMPORT_JD_ACTION.ABORT, label: "Abort", variant: "outline" },
      ],
    });
    return;
  }
  startMatching();
}

function nextApiBullet(fromIndex) {
  return liveMatches.value[fromIndex + 1] || null;
}

function startMatching() {
  // Figma: "Announce the number of extracted responsibilities" then
  // "Ask for a solution for each found responsibility".
  phase.value = IMPORT_JD_PHASE.MATCHING;
  const source = usingLiveApi.value ? liveMatches.value : DEMO_BULLETS;
  if (!source.length) {
    phase.value = IMPORT_JD_PHASE.DONE;
    pushAi({ text: buildDoneText() });
    return;
  }
  bulletIndex.value = 0;
  const bullet = source[0];
  if (usingLiveApi.value) {
    pushAi({
      text: buildFirstLiveMatchingText(
        liveExtractMessage.value,
        bullet,
        liveTotal.value
      ),
      showDropdown: true,
    });
    return;
  }
  pushAi({
    text: buildExtractionIntro(bullet),
    showDropdown: true,
  });
}

function hideActiveDropdown() {
  const last = [...messages.value].reverse().find((message) => message.kind === "ai");
  if (last) last.showDropdown = false;
  const lastWithActions = [...messages.value].reverse().find((message) => message.actions?.length);
  if (lastWithActions) lastWithActions.actions = [];
}

function restoreActiveDropdown() {
  const last = [...messages.value].reverse().find((message) => message.kind === "ai");
  if (last) last.showDropdown = true;
}

function handleAction(type) {
  if (type === IMPORT_JD_ACTION.ABORT) {
    handleClose();
    return;
  }
  if (type === IMPORT_JD_ACTION.UPLOAD_NEW_FILE) {
    emit("request-new-file");
    return;
  }
  if (type === IMPORT_JD_ACTION.RETRY) {
    messages.value = [];
    phase.value = IMPORT_JD_PHASE.ANALYZING;
    extractLive();
    return;
  }
  if (type === IMPORT_JD_ACTION.CONTINUE_AS_IS) {
    hideActiveDropdown();
    pushUser(userActionLabel(type));
    startMatching();
  }
}

function handleDropdownAction(type, event = {}) {
  if (type === IMPORT_JD_ACTION.CREATE_NEW) {
    hideActiveDropdown();
    cancelCreateSuggestion();
    createConfirmed = false;
    createErrorMessage.value = "";
    createInitialName.value = (
      event?.suggested_name ||
      activeBullet.value?.extracted ||
      activeBullet.value?.bullet ||
      ""
    ).trim();
    createInitialContext.value = "";
    createSuggestedByIQ.value = false;
    // Open on the next tick so the menu click is not treated as click-outside
    // on a freshly mounted v-dialog (that hid the modal on the first click).
    nextTick(() => {
      showCreateModal.value = true;
      requestCreateSuggestion();
    });
    return;
  }
  if (type === IMPORT_JD_ACTION.DISCARD) {
    hideActiveDropdown();
    discardConfirmed = false;
    showDiscardModal.value = true;
    return;
  }
  applyDropdownAction(type, event);
}

function handleCreateModalInput(open) {
  if (!open && createSaving.value) return;
  showCreateModal.value = !!open;
  if (!open) cancelCreateSuggestion();
  if (!open && !createConfirmed) restoreActiveDropdown();
}

async function handleCreateModalSave(payload) {
  createConfirmed = true;
  cancelCreateSuggestion();
  const event = {
    suggested_name: payload?.name || createInitialName.value,
    suggested_context: payload?.context ?? createInitialContext.value,
  };
  if (!usingLiveApi.value) {
    showCreateModal.value = false;
    applyDropdownAction(IMPORT_JD_ACTION.CREATE_NEW, event);
    return;
  }
  // Live create-new keeps the dialog open while the request runs so a rejected
  // name (409 duplicate, or any other failure) can be corrected in place with
  // the user's name and context intact.
  createInitialName.value = event.suggested_name;
  createInitialContext.value = event.suggested_context;
  createErrorMessage.value = "";
  createSaving.value = true;
  const applied = await applyDropdownAction(IMPORT_JD_ACTION.CREATE_NEW, event);
  createSaving.value = false;
  if (applied) {
    showCreateModal.value = false;
    return;
  }
  createConfirmed = false;
}

function invalidateCreateSuggestion() {
  suggestGeneration += 1;
  if (suggestTimer) {
    clearTimeout(suggestTimer);
    suggestTimer = null;
  }
  if (suggestAbortController) {
    suggestAbortController.abort();
    suggestAbortController = null;
  }
}

function cancelCreateSuggestion() {
  invalidateCreateSuggestion();
  createLoading.value = false;
}

// ER-12076 / ER-12680: when a user chooses "Create new" for an extracted bullet,
// ask the backend for a shorter IQ-suggested name (<=120 chars) and a context
// (<=1000). Fall back to the Rule 6c first-clause name + full-bullet context
// when the request fails or takes longer than IMPORT_JD_SUGGEST_TIMEOUT_MS. The
// dialog stays in a loading state while the suggestion is in flight. Each
// request owns a generation: closing/reopening the dialog or resetting the
// session invalidates it, so a stale timer or response never touches the
// next session.
async function requestCreateSuggestion() {
  const generation = suggestGeneration;
  try {
    const enabled = await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, true);
    if (enabled === false) return;
  } catch {
    // flag lookup is best-effort; proceed with the suggestion attempt.
  }
  if (generation !== suggestGeneration) return;

  createLoading.value = true;
  const bulletRef = createInitialName.value;
  const controller = new AbortController();
  suggestAbortController = controller;

  const settle = (suggestion) => {
    if (generation !== suggestGeneration) return;
    invalidateCreateSuggestion();
    applySuggestion({ ...suggestion, bullet: bulletRef });
  };

  suggestTimer = setTimeout(() => settle({ fallback: true }), IMPORT_JD_SUGGEST_TIMEOUT_MS);
  try {
    const { data } = await suggestJobDescriptionResponsibility({
      positionId: props.positionId,
      bullet: bulletRef,
      signal: controller.signal,
    });
    settle({
      name: data?.name,
      context: data?.context,
      fallback: !data?.name && !data?.context,
    });
  } catch {
    settle({ fallback: true });
  }
}

function applySuggestion({ name, context, fallback, bullet = "" } = {}) {
  if (createLoading.value) {
    createLoading.value = false;
  }
  const fallbackSuggestion = buildFallbackSuggestion(bullet);
  if (!fallback) {
    createInitialName.value = (name || fallbackSuggestion.name).trim();
    createInitialContext.value = context || "";
    createSuggestedByIQ.value = true;
    return;
  }
  createInitialName.value = fallbackSuggestion.name;
  createInitialContext.value = fallbackSuggestion.context;
  createSuggestedByIQ.value = false;
}

function handleDiscardModalInput(open) {
  showDiscardModal.value = !!open;
  if (!open && !discardConfirmed) restoreActiveDropdown();
}

function handleDiscardModalConfirm() {
  discardConfirmed = true;
  showDiscardModal.value = false;
  applyDropdownAction(IMPORT_JD_ACTION.DISCARD);
}

async function applyDropdownAction(type, event = {}) {
  hideActiveDropdown();
  const userMessageId = pushUser(userActionLabel(type));
  actionPending.value = true;
  scrollThread();

  try {
    if (usingLiveApi.value) {
      const applied = await applyLiveAction(type, event);
      if (!applied) removeMessage(userMessageId);
      return applied;
    }

    const current = DEMO_BULLETS[bulletIndex.value];
    const next = DEMO_BULLETS[bulletIndex.value + 1] || null;
    let text = "";
    if (type === IMPORT_JD_ACTION.ATTACH_ROLE) {
      text = buildAttachRoleResult(current, next);
    } else if (type === IMPORT_JD_ACTION.ATTACH_DIRECT) {
      text = buildAttachDirectResult(current, next);
    } else if (type === IMPORT_JD_ACTION.CREATE_NEW) {
      text = buildCreateNewResult(current, next, undefined, event);
    } else {
      text = buildDiscardResult(current, next);
    }

    if (next) {
      // ER-11670 Rule 6a: confirmation line + blank line + "Now the next
      // one." + the next bullet's counter-prefixed assessment, all in one
      // AI turn.
      bulletIndex.value += 1;
      pushAi({ text, showDropdown: true });
      return true;
    }

    // Last bullet: post its confirmation line on its own, then go straight
    // to the Rule 9 closing summary — no "Now the next one." in between.
    phase.value = IMPORT_JD_PHASE.DONE;
    pushAi({ text });
    pushAi({ text: buildDoneText() });
    return true;
  } finally {
    actionPending.value = false;
  }
}

const GENERIC_ACTION_ERROR = "I'm sorry, I couldn't apply that action. Please try again.";

// Resolves true when the action was applied (or failed in a way the chat already
// reported) and false when a create-new failure is surfaced inline in the dialog.
async function applyLiveAction(type, event = {}) {
  const current = liveMatches.value[bulletIndex.value] || {};
  const next = nextApiBullet(bulletIndex.value);
  const payload = {
    action: type,
    extracted: current.extracted,
    next,
    total: liveTotal.value,
  };
  if (type === IMPORT_JD_ACTION.ATTACH_ROLE) {
    payload.role_id = event?.role_id ?? activeCoveringRoles.value[0]?.role_id;
  }
  if (type === IMPORT_JD_ACTION.ATTACH_DIRECT) {
    payload.responsibility_id =
      event?.responsibility_id ??
      current.matched_responsibility_id ??
      current.matchedResponsibilityId;
  }
  if (type === IMPORT_JD_ACTION.CREATE_NEW) {
    payload.suggested_name = event?.suggested_name || current.extracted;
    payload.suggested_context = event?.suggested_context ?? "";
  }
  try {
    const { data } = await executeJobDescriptionSolution({
      positionId: props.positionId,
      payload,
    });
    if (data?.done || !data?.next) {
      phase.value = IMPORT_JD_PHASE.DONE;
      pushAi({ text: data?.message || buildDoneText() });
      return true;
    }
    const nextIndex = liveMatches.value.findIndex(
      (row) =>
        row.index === data.next.index ||
        row.extracted === data.next.extracted
    );
    bulletIndex.value = nextIndex >= 0 ? nextIndex : bulletIndex.value + 1;
    pushAi({ text: data.message, showDropdown: true });
    return true;
  } catch (error) {
    console.error("Failed to execute job description action:", error);
    if (type === IMPORT_JD_ACTION.CREATE_NEW) {
      createErrorMessage.value = getResponsibilityCreateErrorMessage(error, GENERIC_ACTION_ERROR);
      return false;
    }
    restoreActiveDropdown();
    pushAi({ text: GENERIC_ACTION_ERROR });
    return true;
  }
}

function handleClose() {
  resetSession();
  emit("close");
}
</script>

<style scoped>
.ijd-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.ijd-overlay__shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #ffffff;
}

.ijd-overlay__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.ijd-chat {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
  padding: 0 30px;
  border-right: 1px solid #eaecf0;
}

.ijd-chat--split {
  width: 50%;
  flex: 0 0 50%;
}

.ijd-chat__pinned {
  flex-shrink: 0;
  padding-top: 28px;
}

.ijd-chat__thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 17px 0 28px;
}

.ijd-turn {
  width: 100%;
}

.ijd-turn + .ijd-turn {
  margin-top: 10px;
}

.ijd-ai {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 5px 10px 5px 10px;
}

.ijd-ai--pending {
  overflow: visible;
  padding-top: 8px;
  padding-bottom: 8px;
}

.ijd-ai__icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
}

.ijd-ai__icon :deep(svg) {
  width: 28px;
  height: 28px;
}

.ijd-ai__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.ijd-ai__text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #101828;
  white-space: pre-wrap;
}

.ijd-ai__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ijd-user {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.ijd-user__bubble {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 12px 16px;
  background: #f2f4f7;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #344054;
}

.ijd-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 50%;
  flex: 0 0 50%;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border-left: 1px solid #eaecf0;
  background: #ffffff;
}

.ijd-preview__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 3px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  cursor: pointer;
}

.ijd-preview__close:hover {
  background: #f9fafb;
}

.ijd-preview__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
}
</style>
